import React, { useEffect, useState } from "react";
import { Tag, Plus, Pencil, Trash2, Loader2, X, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Switch } from "../../components/ui/switch";

const LANGS = ["tr", "en", "de"];

export default function AdminBlogCategories() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/content/blog-categories");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const create = () => setEditing({
        name: { tr: "", en: "", de: "" },
        description: { tr: "", en: "", de: "" },
        slug: "",
        sort_order: items.length + 1,
        is_active: true,
        isNew: true,
    });

    const save = async () => {
        if (!editing.name?.tr) {
            toast.error("Kategori adı (TR) zorunlu.");
            return;
        }
        try {
            const payload = { ...editing };
            delete payload.isNew;
            if (!editing.isNew) payload.id = editing.id;
            await api.post("/admin/content/blog-categories", payload);
            toast.success("Kaydedildi.");
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Kategori silinecek. Onaylıyor musunuz?")) return;
        try {
            await api.delete(`/admin/content/blog-categories/${id}`);
            toast.success("Silindi.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="admin-blog-categories-page">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy inline-flex items-center gap-2">
                        <Tag className="w-6 h-6 text-teal" /> Blog Kategorileri
                    </h1>
                    <p className="text-sm text-deep-blue/70 mt-1">Yazıları gruplamak için kullanılan etiket setleri (TR/EN/DE).</p>
                </div>
                <button
                    onClick={create}
                    className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-2"
                    data-testid="blog-cat-new"
                >
                    <Plus className="w-4 h-4" /> Yeni Kategori
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-slate-100 overflow-hidden">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Henüz kategori yok.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="blog-cats-table">
                        <thead>
                            <tr className="bg-light-bg text-left text-xs uppercase tracking-wider text-deep-blue/70">
                                <th className="px-4 py-3">Sıra</th>
                                <th className="px-4 py-3">Ad (TR / EN / DE)</th>
                                <th className="px-4 py-3">Slug</th>
                                <th className="px-4 py-3">Aktif</th>
                                <th className="px-4 py-3 w-32">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-t border-slate-100">
                                    <td className="px-4 py-3 font-data text-deep-blue">{c.sort_order}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-dark-navy text-sm">{c.name?.tr || "—"}</div>
                                        <div className="text-xs text-deep-blue/60">{c.name?.en} · {c.name?.de}</div>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-deep-blue/70">/{c.slug}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block w-2 h-2 rounded-full ${c.is_active ? "bg-data-green" : "bg-slate-400"}`}></span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => setEditing({ ...c, isNew: false })} title="Düzenle" className="p-1.5 rounded-md hover:bg-light-bg" data-testid={`blog-cat-edit-${c.id}`}>
                                                <Pencil className="w-4 h-4 text-deep-blue" />
                                            </button>
                                            <button onClick={() => remove(c.id)} title="Sil" className="p-1.5 rounded-md hover:bg-light-bg">
                                                <Trash2 className="w-4 h-4 text-data-red" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="blog-cat-editor">
                    <div className="bg-white rounded-lg shadow-2xl border border-slate-200 max-w-xl w-full my-8">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-dark-navy">{editing.isNew ? "Yeni Kategori" : "Kategori Düzenle"}</h2>
                            <button onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-light-bg">
                                <X className="w-4 h-4 text-deep-blue" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            {LANGS.map((l) => (
                                <div key={l}>
                                    <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Ad ({l})</label>
                                    <input
                                        value={editing.name?.[l] || ""}
                                        onChange={(e) => setEditing({ ...editing, name: { ...(editing.name || {}), [l]: e.target.value } })}
                                        className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                        data-testid={`blog-cat-name-${l}`}
                                    />
                                </div>
                            ))}
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Slug (boş bırakılırsa otomatik)</label>
                                    <input
                                        value={editing.slug || ""}
                                        onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Sıra</label>
                                    <input
                                        type="number"
                                        value={editing.sort_order ?? 0}
                                        onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })}
                                        className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-data"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-between bg-light-bg rounded-md p-3">
                                <span className="text-sm font-semibold text-dark-navy">Aktif</span>
                                <Switch checked={editing.is_active !== false} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md bg-light-bg text-deep-blue text-sm font-semibold">İptal</button>
                            <button
                                onClick={save}
                                className="px-4 py-2 rounded-md bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold inline-flex items-center gap-1"
                                data-testid="blog-cat-save"
                            >
                                <Save className="w-4 h-4" /> Kaydet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
